<template>
  <div class="big-screen-container">
    <!-- 视频页面（作为轮播的一页） -->
        <SleepViewVideo
          v-if="isVideoPage"
          ref="videoView"
          :video-list="videoList"
          :current-video-index="currentVideoIndex"
          :preloaded-video="preloadVideoElement"
          :resume-time="currentVideoResumeTime"
          :video-switch-mode="rotationSeconds && rotationSeconds.videoSwitchMode"
          @video-ended="handleVideoEnded"
          @video-playing-changed="handleVideoPlayingChanged"
          @video-timeupdate="handleVideoTimeUpdate"
        />
    
    <!-- 全屏欢迎语（作为轮播的一页） -->
    <SleepViewFullscreenWelcome
      v-else-if="isFullscreenWelcomePage"
      :welcome-message="welcomeConfig && welcomeConfig.welcomeText ? welcomeConfig.welcomeText : ''"
      :welcome-animation-state="welcomeAnimationState"
      :welcome-message-style="welcomeMessageStyle"
    />
    
    <!-- 半屏欢迎语或默认视图（患者模块页面） -->
    <component
      v-else
      :is="(welcomeMessage || welcomeAnimationState === 'leaving') ? 'SleepViewWelcome' : 'SleepViewDefault'"
      ref="sleepView"
      :displayed-appointments="displayedAppointments"
      :current-time="currentTime"
      :current-weekday="currentWeekday"
      :current-date="currentDate"
      :ongoing-treatment-count="ongoingTreatmentCount"
      :is-sliding="isSliding"
      :welcome-message="welcomeMessage"
      :welcome-animation-state="welcomeAnimationState"
      :welcome-message-style="welcomeMessageStyle"
      :helpers="helpers"
      :current-page="currentPage"
      :page-epoch="pageEpoch"
    />
    
    <!-- 隐藏的预加载视频元素（使用v-show避免元素销毁重建，完全禁用自动加载） -->
    <video
      v-show="videoList && videoList.length > 0 && !isVideoPage && !isVideoPlaying && preloadVideoUrl"
      ref="preloadVideo"
      preload="none"
      muted
      playsinline
      style="position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; z-index: -1;"
      @loadeddata="onPreloadVideoLoaded"
      @canplaythrough="onPreloadVideoCanPlayThrough"
      @error="onPreloadVideoError"
      @loadstart="onPreloadVideoLoadStart"
    ></video>
  </div>
</template>

<script>
import * as sleepViewUtils from '@/utils/sleepViewUtils';
import * as sleepDataUtils from '@/utils/sleepDataUtils';
import axios from 'axios';
import realtimeAPI from '@/api/realtime';
import SleepViewWelcome from '@/components/SleepViewWelcome.vue';
import SleepViewDefault from '@/components/SleepViewDefault.vue';
import SleepViewFullscreenWelcome from '@/components/SleepViewFullscreenWelcome.vue';
import SleepViewVideo from '@/components/SleepViewVideo.vue';
import config from '@/config';
import { getWelcomeConfigByStoreId, getUserStoreIds, getWelcomeConfigs, removeWelcomeConfigByStoreId } from '@/utils/auth';
import { parseApiResponse, handleApiError, snakeToCamel } from '@/utils/apiResponse';

// 导入 composables
import useTimers from '@/composables/useTimers';
import useVideoManager from '@/composables/useVideoManager';
import useWelcomeMessage from '@/composables/useWelcomeMessage';
import useAppointmentData from '@/composables/useAppointmentData';
import useRecoveryCount from '@/composables/useRecoveryCount';
import usePageRotation from '@/composables/usePageRotation';
import useBubbles from '@/composables/useBubbles';
import useSystemLog from '@/composables/useSystemLog';
import useChartManager from '@/composables/useChartManager';
import useBrainwaveData from '@/composables/useBrainwaveData';
import useRealtimeData from '@/composables/useRealtimeData';
import useAppointmentProcessing from '@/composables/useAppointmentProcessing';
import useDisplayedAppointments from '@/composables/useDisplayedAppointments';

// 合并 composables
const timersComposable = useTimers();
const videoManagerComposable = useVideoManager();
const welcomeMessageComposable = useWelcomeMessage();
const appointmentDataComposable = useAppointmentData();
const recoveryCountComposable = useRecoveryCount();
const pageRotationComposable = usePageRotation();
const bubblesComposable = useBubbles();
const systemLogComposable = useSystemLog();
const chartManagerComposable = useChartManager();
const brainwaveDataComposable = useBrainwaveData();
const realtimeDataComposable = useRealtimeData();
const appointmentProcessingComposable = useAppointmentProcessing();
const displayedAppointmentsComposable = useDisplayedAppointments();

export default {
  components: { SleepViewWelcome, SleepViewDefault, SleepViewFullscreenWelcome, SleepViewVideo },
  props: {
    storeId: {
      type: [String, Number],
      default: null
    }
  },
  // 合并 composables 的 data
  data() {
    return {
      // 从 composables 合并的数据
      ...timersComposable.data(),
      ...videoManagerComposable.data(),
      ...welcomeMessageComposable.data(),
      ...appointmentDataComposable.data(),
      ...recoveryCountComposable.data(),
      ...pageRotationComposable.data(),
...bubblesComposable.data(),
      ...systemLogComposable.data(),

      // 主组件特有的数据
      maxDisplayCount: 4, // 每页显示4个患者模块
      maxBubbles: 2, // 最多显示2个气泡
      pageTransition: {
        active: false,
        direction: 'next' // 'next' 或 'prev'
      },
      pendingPage: null, // 待切换的页面，用于延迟内容更新
      realtimeData: {}, // 存储实时状态数据
      // 睡眠状态模拟数据
      sleepStates: ['清醒', '浅睡', '深睡'], // 三种睡眠状态
      sleepStateIndex: 0, // 当前睡眠状态索引
      // 当前正在播放的视频索引，用于多视频轮播
      currentVideoIndex: 0,
      // 视频断点（仅当前运行内生效；刷新不记忆）
      videoResumeTimeById: {},

      // 轮播时长配置（秒）：可被后台配置覆盖
      rotationSeconds: {
        welcomeFullscreen: 15,
        patientPage: 15,
        videoPage: 30,
        videoSwitchMode: 'FIXED_WINDOW' // FIXED_WINDOW | PLAY_TO_END
      },
      cleanupPreloadVideoTimer: null, // 清理预加载视频时的 300ms 延迟定时器，销毁时需清理
      newAppointmentResetTimers: [] // 新预约 isNew 的 3 秒重置定时器 id，销毁时需清理
    };
  },
  mounted() {
    this.addSystemLog('系统初始化完成');
    this.addSystemLog('预约到点自动显示功能已启用');
    
    // 如果有门店ID，记录到日志中
    const effectiveStoreId = this.effectiveStoreId;
    if (effectiveStoreId) {
      this.addSystemLog(`当前门店ID: ${effectiveStoreId}`);
    } else {
      this.addSystemLog('未检测到门店ID');
    }

    // 获取初始数据
    this.fetchAppointmentData();
    this.fetchTodayAppointmentData(); // 获取当日预约记录
    this.fetchWelcomeMessage(); // 获取欢迎语
    this.fetchVideoList(); // 获取视频列表
    this.fetchRotationSeconds(); // 获取轮播时长配置
    
    // 预加载气泡背景图片，避免重复加载
    if (this.preloadBubbleImage) {
      this.preloadBubbleImage();
    }
    
    // 延迟2秒后获取初始实时数据（确保预约数据已加载）
    if (process.env.NODE_ENV !== 'development') {
      setTimeout(() => {
        if (this.fetchRealtimeData && typeof this.fetchRealtimeData === 'function') {
          this.fetchRealtimeData();
        }
      }, 2000);
    }

    // 初始化所有定时器（使用 composable 中的方法）
    this.initAllTimers();

    this.initRecoveryPersist();

    if (process.env.NODE_ENV === 'development') {
      this.addSystemLog('开发环境：启用脑波模拟数据');
    } else {
      this.addSystemLog('生产环境：使用真实API数据');
    }

    this.$nextTick(() => {
      this.startRotation();
      this.addSystemLog('模块轮播功能已启动');
      if (this.initCharts) {
        this.initCharts(); // 初始化所有图表
      }
      
      // 初始化所有预约的气泡数组
      if (this.appointmentData && this.appointmentData.length > 0) {
        this.appointmentData.forEach(item => {
          if (!item.bubbles) {
            item.bubbles = [];
          }
        });
      }
      
      if (this.updateBubbles) {
        this.updateBubbles(); // 立即更新一次气泡
      }
    });
  },

  beforeDestroy() {
    if (this.isSliding && this.cancelSlideAnimation) {
      this.cancelSlideAnimation('组件销毁');
    }
    if (this.checkSlidingTimer) {
      clearInterval(this.checkSlidingTimer);
      this.checkSlidingTimer = null;
    }
    if (this.rotationSlidingRetryTimer) {
      clearTimeout(this.rotationSlidingRetryTimer);
      this.rotationSlidingRetryTimer = null;
    }
    if (this.welcomeAnimationResetTimer) {
      clearTimeout(this.welcomeAnimationResetTimer);
      this.welcomeAnimationResetTimer = null;
    }
    if (this.slideCompleteBubbleTimers && this.slideCompleteBubbleTimers.length > 0) {
      this.slideCompleteBubbleTimers.forEach(tid => clearTimeout(tid));
      this.slideCompleteBubbleTimers = [];
    }
    if (this.cleanupPreloadVideoTimer) {
      clearTimeout(this.cleanupPreloadVideoTimer);
      this.cleanupPreloadVideoTimer = null;
    }
    
    // 清理所有定时器（使用 composable 中的方法）
    this.clearAllTimers();
    
    // 清理预加载视频（组件销毁时强制清理）
    if (this.cleanupPreloadVideo) {
      this.cleanupPreloadVideo(true);
    }
    if (this.newAppointmentResetTimers && this.newAppointmentResetTimers.length > 0) {
      this.newAppointmentResetTimers.forEach(tid => clearTimeout(tid));
      this.newAppointmentResetTimers = [];
    }
    
    // 清理图表
    if (this.disposeCharts) {
      this.disposeCharts();
    }
    
    // 调用 composables 的 beforeDestroy（如果有）
    if (timersComposable.beforeDestroy) {
      timersComposable.beforeDestroy.call(this);
    }
  },
  

  // 合并 composables 的 methods
  methods: {
    // 从 composables 合并的方法
    ...timersComposable.methods,
    ...videoManagerComposable.methods,
    ...welcomeMessageComposable.methods,
    ...appointmentDataComposable.methods,
    ...recoveryCountComposable.methods,
    ...pageRotationComposable.methods,
    ...bubblesComposable.methods,
    ...systemLogComposable.methods,
    ...chartManagerComposable.methods,
    ...brainwaveDataComposable.methods,
    ...realtimeDataComposable.methods,
    ...appointmentProcessingComposable.methods,

    // 气泡初始化方法已提取到 useBubbles composable

    // 播放欢迎语进入动画
    playWelcomeEnterAnimation() {
      this.welcomeAnimationState = 'entering';
      
      // 清除之前的动画定时器
      if (this.welcomeAnimationTimer) {
        clearTimeout(this.welcomeAnimationTimer);
      }
      
      // 动画完成后重置状态
      this.welcomeAnimationTimer = setTimeout(() => {
        this.welcomeAnimationState = '';
        
        // 欢迎语显示后，需要重新调整所有图表尺寸
        this.$nextTick(() => {
          this.resizeAllCharts();
        });
      }, 600); // 动画持续时间
    },
    
    // 播放欢迎语退出动画
    playWelcomeLeaveAnimation() {
      this.welcomeAnimationState = 'leaving';
      
      // 清除之前的动画定时器
      if (this.welcomeAnimationTimer) {
        clearTimeout(this.welcomeAnimationTimer);
      }
      
      // 动画完成后重置状态并清空欢迎语
      this.welcomeAnimationTimer = setTimeout(() => {
        this.welcomeAnimationState = '';
        this.welcomeMessage = '';
        
        // 欢迎语隐藏后，需要重新调整所有图表尺寸
        this.$nextTick(() => {
          this.resizeAllCharts();
        });
      }, 500); // 动画持续时间
    },
    
    // 获取启用的欢迎语
    async fetchWelcomeMessage() {
      if (this.isSliding) {
        return; // 滑动期间暂停欢迎语更新，避免触发不必要渲染
      }
      
      const storeId = this.effectiveStoreId;
      if (!storeId) {
        // 如果没有门店ID，尝试从登录信息中获取
        const storeIds = getUserStoreIds();
        if (storeIds.length === 0) {
          console.warn('未找到门店ID，无法获取欢迎配置');
          this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
          this.welcomeConfig = null;
          this.handleWelcomeMessageChange('');
          return;
        }
      }
      
      // 优先调用 API 获取最新数据，确保与数据库同步
      try {
        const apiUrl = config.getApiUrl(
          config.apiEndpoints.welcomeConfig.enabled,
          storeId
        );
        
        const response = await axios.get(apiUrl);
        
        // 接口返回 ApiResponse 格式：{ code: 200, message: "...", data: {...} }
        const result = parseApiResponse(response);
        
        if (!result.success || !result.data) {
          console.warn('欢迎语接口返回数据为空');
          // 清除缓存中的欢迎配置
          removeWelcomeConfigByStoreId(storeId);
          // 更新 lastWelcomeConfig
          this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
          // 清空 welcomeConfig
          this.welcomeConfig = null;
          this.handleWelcomeMessageChange('');
          return;
        }
        
        // 将下划线字段名转换为驼峰命名
        const welcomeConfig = snakeToCamel(result.data);
        
        // 关键修复：检查 is_enabled 字段，只有当启用时才显示欢迎语
        const isEnabled = welcomeConfig.isEnabled !== undefined 
          ? (welcomeConfig.isEnabled === true || welcomeConfig.isEnabled === 1 || welcomeConfig.isEnabled === '1')
          : true; // 如果没有 is_enabled 字段，默认启用（向后兼容）
        
        if (!isEnabled) {
          console.log('欢迎语未启用，不显示欢迎语', {
            storeId,
            isEnabled: welcomeConfig.isEnabled
          });
          // 如果未启用，清空欢迎语
          this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
          this.welcomeConfig = null;
          this.handleWelcomeMessageChange('');
          return;
        }
        
        // 存储上一次的配置，用于检测变化
        this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
        
        // 存储完整的配置信息（包含 isFullscreen 字段），确保 isFullscreen 为数字类型
        this.welcomeConfig = {
          ...welcomeConfig,
          isFullscreen: welcomeConfig.isFullscreen !== undefined 
            ? (typeof welcomeConfig.isFullscreen === 'number' 
                ? welcomeConfig.isFullscreen 
                : (welcomeConfig.isFullscreen === true || welcomeConfig.isFullscreen === 'true' ? 1 : parseInt(welcomeConfig.isFullscreen) || 0))
            : 0
        };
        
        // 根据接口返回的数据结构处理欢迎语
        let newWelcomeMessage = '';
        
        // 处理API返回的welcomeText字段（已转换为驼峰命名）
        if (welcomeConfig.welcomeText) {
          newWelcomeMessage = welcomeConfig.welcomeText;
        }
        
        // 处理欢迎语状态变化
        this.handleWelcomeMessageChange(newWelcomeMessage);
        
      } catch (error) {
        // 接口调用失败时的处理
        const errorResult = handleApiError(error);
        
        if (error.response && error.response.status === 404) {
          // 404表示该门店没有配置欢迎语，清除缓存
          removeWelcomeConfigByStoreId(storeId);
          // 更新 lastWelcomeConfig
          this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
          // 清空 welcomeConfig
          this.welcomeConfig = null;
          this.handleWelcomeMessageChange('');
        } else {
          // 其他错误（网络错误等），fallback 到缓存配置作为降级方案
          const loginWelcomeConfig = getWelcomeConfigByStoreId(storeId);
          if (loginWelcomeConfig) {
            // 使用登录时返回的欢迎配置作为降级方案
            this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
            
            // 存储完整的配置信息（包含 isFullscreen 字段），确保 isFullscreen 为数字类型
            this.welcomeConfig = {
              ...loginWelcomeConfig,
              isFullscreen: loginWelcomeConfig.isFullscreen !== undefined 
                ? (typeof loginWelcomeConfig.isFullscreen === 'number' 
                    ? loginWelcomeConfig.isFullscreen 
                    : parseInt(loginWelcomeConfig.isFullscreen) || 0)
                : 0
            };
            
            // 处理欢迎语内容（兼容下划线和驼峰命名）
            const newWelcomeMessage = loginWelcomeConfig.welcomeText || loginWelcomeConfig.welcome_text || '';
            this.handleWelcomeMessageChange(newWelcomeMessage);
          } else {
            // 既没有API数据，也没有缓存，清空欢迎语
            this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
            this.welcomeConfig = null;
            this.handleWelcomeMessageChange('');
          }
        }
      }
    },
    
    // 处理欢迎语状态变化的统一方法
    handleWelcomeMessageChange(newWelcomeMessage) {
      // 检测 isFullscreen 是否发生变化
      const oldIsFullscreen = this.lastWelcomeConfig && 
        (this.lastWelcomeConfig.isFullscreen === 1 || this.lastWelcomeConfig.isFullscreen === '1');
      const newIsFullscreen = this.welcomeConfig && 
        (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1');
      const isFullscreenChanged = oldIsFullscreen !== newIsFullscreen;
      
      // 检测欢迎语内容是否发生变化
      const welcomeMessageChanged = this.lastWelcomeMessage !== newWelcomeMessage;
      
      // 如果欢迎语内容或 isFullscreen 发生变化，都需要处理
      if (welcomeMessageChanged || isFullscreenChanged) {
        // 更新状态
        this.lastWelcomeMessage = newWelcomeMessage;
        
        // 获取 isFullscreen 配置（0=半屏，1=全屏），支持数字和字符串类型
        const isFullscreen = this.welcomeConfig && 
          (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1');
        
        // 如果是全屏模式，不显示半屏欢迎语
        if (isFullscreen) {
          this.welcomeMessage = ''; // 全屏模式下不显示半屏欢迎语
          this.maxDisplayCount = 4; // 全屏模式下每页显示4个患者模块
          
          // 如果从半屏切换到全屏，且当前在患者模块页面，需要检查是否需要切换到全屏欢迎语页
          // 注意：初始化时（lastWelcomeConfig === null）不执行页面切换，确保从患者页开始
          if (isFullscreenChanged && !oldIsFullscreen && this.lastWelcomeConfig !== null) {
            // 计算治疗中患者数量
            const ongoingPatients = this.activeAppointments.filter(item => 
              this.getTreatmentStatus(item) === '治疗中'
            );
            // 全屏模式下每页4个
            const patientPages = Math.ceil(ongoingPatients.length / 4);
            // 如果当前在最后一页患者模块，立即切换到全屏欢迎语页
            if (this.currentPage === patientPages - 1) {
              this.currentPage = patientPages;
              this.addSystemLog('从半屏模式切换到全屏模式，切换到全屏欢迎语页');
            }
          }
        } else {
          // 半屏模式，使用原有逻辑
          if (newWelcomeMessage && !this.welcomeMessage) {
            // 从无到有：播放进入动画
            this.welcomeMessage = newWelcomeMessage;
            this.playWelcomeEnterAnimation();
            this.addSystemLog(`门店${this.effectiveStoreId}欢迎语已启用并显示（半屏模式）`);
          } else if (!newWelcomeMessage && this.welcomeMessage) {
            // 从有到无：播放退出动画
            this.playWelcomeLeaveAnimation();
            this.addSystemLog(`门店${this.effectiveStoreId}欢迎语已禁用并隐藏`);
          } else if (newWelcomeMessage && this.welcomeMessage && newWelcomeMessage !== this.welcomeMessage) {
            // 内容变化：直接更新
            this.welcomeMessage = newWelcomeMessage;
            this.addSystemLog(`门店${this.effectiveStoreId}欢迎语内容已更新`);
          }
          this.maxDisplayCount = newWelcomeMessage ? 2 : 4;
          
          // 如果从全屏切换到半屏，且当前在全屏欢迎语页，需要切换回患者模块页
          // 注意：初始化时（lastWelcomeConfig === null）不执行页面切换，确保从患者页开始
          if (isFullscreenChanged && oldIsFullscreen && this.lastWelcomeConfig !== null) {
            // 计算治疗中患者数量
            const ongoingPatients = this.activeAppointments.filter(item => 
              this.getTreatmentStatus(item) === '治疗中'
            );
            // 半屏模式下每页2个
            const patientPages = Math.ceil(ongoingPatients.length / 2);
            // 如果当前在全屏欢迎语页，切换回最后一页患者模块
            if (this.currentPage === patientPages) {
              this.currentPage = Math.max(0, patientPages - 1);
              this.addSystemLog('从全屏模式切换到半屏模式，切换回患者模块页');
              // 重新初始化图表
              this.$nextTick(() => {
                this.initCharts();
              });
            }
          }
        }
      } else {
        // 即使欢迎语内容没变，也要检查 isFullscreen 是否变化（这个分支现在应该不会执行，因为上面已经处理了）
        // 但为了安全起见，保留这个逻辑，并确保支持字符串 '1'
        const isFullscreen = this.welcomeConfig && 
          (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1');
        if (isFullscreen) {
          this.welcomeMessage = '';
          this.maxDisplayCount = 4;
        } else {
          this.maxDisplayCount = newWelcomeMessage ? 2 : 4;
        }
      }
    },
    
    
    // 获取预加载视频的URL（内部方法，用于计算URL）
    getPreloadVideoUrl() {
      if (!this.videoList || this.videoList.length === 0) {
        return '';
      }
      const firstVideo = this.videoList[0];
      if (firstVideo && firstVideo.id) {
        return config.getApiUrl(config.apiEndpoints.welcomeVideo.get, firstVideo.id);
      }
      return '';
    },
    
    // 更新预加载视频URL（只在URL真正变化时更新）
    updatePreloadVideoUrl() {
      const newUrl = this.getPreloadVideoUrl();
      
      // 严格比较URL，确保只在真正变化时才更新
      if (newUrl === this.preloadVideoUrl && newUrl === this.lastPreloadVideoUrl) {
        // URL完全相同，不做任何操作
        return;
      }
      
      const oldUrl = this.preloadVideoUrl;
      
      // 如果新URL和上次设置的URL相同，说明是重复调用，跳过
      if (newUrl === this.lastPreloadVideoUrl && newUrl) {
        return;
      }
      
      // 立即更新URL，确保preloadVideoUrl不为空
      this.preloadVideoUrl = newUrl;
      this.lastPreloadVideoUrl = newUrl; // 记录上次设置的URL
      
      // 如果URL为空，立即清空状态
      if (!newUrl) {
        this.videoPreloadStatus = 'idle';
        this.videoPreloadRetryCount = 0;
        this.currentPreloadVideoSrc = '';
        // 清空视频元素的src，避免继续请求
        if (this.$refs.preloadVideo) {
          this.$refs.preloadVideo.src = '';
          this.currentPreloadVideoSrc = '';
        }
        return;
      }
        
      // 如果URL变化，重置状态以便重新加载
      if (oldUrl) {
        // URL改变了，重置状态
        this.videoPreloadStatus = 'idle';
        this.videoPreloadRetryCount = 0;
        this.currentPreloadVideoSrc = '';
        this.addSystemLog(`视频URL已更新: ${oldUrl} -> ${newUrl}`);
      } else {
        // 从无到有，初始化状态
        this.videoPreloadStatus = 'idle';
        this.videoPreloadRetryCount = 0;
        this.currentPreloadVideoSrc = '';
        this.addSystemLog(`视频URL已设置: ${newUrl}`);
      }
      
      // 注意：不在这里设置src，只在preloadVideo()中真正需要加载时才设置
      // 这样可以避免元素重建时自动触发请求
      
      // 延迟调用preloadVideo()，确保其他关键资源优先加载
      if (this.videoPreloadTimer) {
        clearTimeout(this.videoPreloadTimer);
      }
      this.videoPreloadTimer = setTimeout(() => {
        this.videoPreloadTimer = null;
        // 延迟500ms后再调用预加载
        if (this.preloadVideoUrl && !this.isVideoPage) {
          this.preloadVideo();
        }
      }, 500); // 延迟500ms
    },
    
    // 预加载视频开始加载
    onPreloadVideoLoadStart() {
      // 记录请求开始，但不做任何操作，避免触发循环
      // 注意：loadstart事件会在每次load()调用时触发，这是正常的
    },
    
    // 预加载视频元数据加载完成
    onPreloadVideoLoaded() {
      // 元数据加载完成，但视频内容可能还未完全加载
      if (this.videoPreloadStatus === 'idle') {
        this.videoPreloadStatus = 'loading';
      }
      console.log('[视频预加载] 元数据加载完成', {
        url: this.preloadVideoUrl,
        readyState: this.$refs.preloadVideo ? this.$refs.preloadVideo.readyState : 'unknown',
        timestamp: new Date().toISOString()
      });
    },
    
    // 预加载视频可以完整播放（关键优化点）
    onPreloadVideoCanPlayThrough() {
      this.videoPreloadStatus = 'loaded';
      this.videoPreloadRetryCount = 0; // 重置重试次数
      this.isPreloadVideoRequesting = false; // 重置请求锁
      
      if (this.$refs.preloadVideo) {
        this.preloadVideoElement = this.$refs.preloadVideo;
        // 记录当前src，避免重复设置
        this.currentPreloadVideoSrc = this.$refs.preloadVideo.src || this.preloadVideoUrl;
      }
      
      this.addSystemLog('视频预加载成功，已缓存到本地');
    },
    
    // 预加载视频加载失败
    onPreloadVideoError(error) {
      this.isPreloadVideoRequesting = false; // 重置请求锁
      
      const errorDetails = {
        message: error.message || '未知错误',
        type: error.type || 'unknown',
        target: error.target ? {
          src: error.target.src,
          networkState: error.target.networkState,
          readyState: error.target.readyState,
          error: error.target.error
        } : null,
        videoUrl: this.preloadVideoUrl
      };
      
      // 检查是否是AbortError（主动取消），不重试
      const isAbortError = errorDetails.type === 'abort' || 
                          errorDetails.message?.includes('abort') ||
                          errorDetails.message?.includes('Abort');
      
      if (isAbortError) {
        // 请求被取消，不重试
        this.videoPreloadStatus = 'idle';
        this.videoPreloadRetryCount = 0;
        return;
      }
      
      this.addSystemLog(`视频预加载失败: ${errorDetails.message || '未知错误'}`);
      
      // 如果重试次数未超过限制，延迟重试（大幅增加延迟）
      if (this.videoPreloadRetryCount < this.maxPreloadRetryCount) {
        this.videoPreloadRetryCount++;
        const retryDelay = this.videoPreloadRetryCount * 10000; // 递增延迟：10秒、20秒、30秒
        this.addSystemLog(`视频预加载将在${retryDelay / 1000}秒后重试（第${this.videoPreloadRetryCount}次）`);
        if (this.videoPreloadRetryTimer) {
          clearTimeout(this.videoPreloadRetryTimer);
          this.videoPreloadRetryTimer = null;
        }
        this.videoPreloadRetryTimer = setTimeout(() => {
          this.videoPreloadRetryTimer = null;
          if (this.preloadVideoUrl &&
              this.videoPreloadStatus === 'error' &&
              !this.isPreloadVideoRequesting) {
            this.videoPreloadStatus = 'idle';
            this.preloadVideo();
          }
        }, retryDelay);
      } else {
        // 超过最大重试次数，标记为错误状态
        this.videoPreloadStatus = 'error';
        this.addSystemLog('视频预加载失败，已达到最大重试次数');
      }
    },
    
    // 开始预加载视频
    preloadVideo() {
      // 首先检查URL是否已设置，如果没有URL则直接返回
      if (!this.preloadVideoUrl) {
        return;
      }
      
      // 如果正在请求，直接返回，防止并发请求
      if (this.isPreloadVideoRequesting) {
        return;
      }
      
      // 如果当前在视频页，不预加载
      if (this.isVideoPage) {
        return;
      }
      
      // 如果已经在加载或已加载，检查URL是否变化
      if (this.videoPreloadStatus === 'loading' || this.videoPreloadStatus === 'loaded') {
        // 检查视频URL是否变化，如果变化了才需要重新加载
        const preloadedUrl = this.preloadVideoElement && this.preloadVideoElement.src;
        
        if (this.preloadVideoUrl && this.preloadVideoUrl === preloadedUrl) {
          // URL没变，不需要重新加载
          return;
        }
        // URL变了，需要重新加载，重置状态和重试次数
        this.videoPreloadStatus = 'idle';
        this.videoPreloadRetryCount = 0;
        this.isPreloadVideoRequesting = false; // 重置请求锁
      }
      
      // 清除之前的延迟定时器
      if (this.videoPreloadTimer) {
        clearTimeout(this.videoPreloadTimer);
        this.videoPreloadTimer = null;
      }
      
      // 延迟500ms后再开始预加载，确保关键数据（预约数据、实时数据）优先加载
      this.videoPreloadTimer = setTimeout(() => {
        this.videoPreloadTimer = null;
        
        // 延迟后再次检查URL，确保URL仍然有效
        if (!this.preloadVideoUrl) {
          this.addSystemLog('视频预加载取消：URL为空');
          return;
        }
        
        // 再次检查是否在视频页
        if (this.isVideoPage) {
          return;
        }
        
        // 再次检查是否正在请求
        if (this.isPreloadVideoRequesting) {
          return;
        }
        
        // 检查视频元素是否已加载
        if (this.$refs.preloadVideo) {
          const videoElement = this.$refs.preloadVideo;
          const currentSrc = videoElement.src || '';
          const targetSrc = this.preloadVideoUrl;
          
          // 如果src相同且视频已加载，不需要重新加载
          if (currentSrc === targetSrc && videoElement.readyState >= 3) {
            this.videoPreloadStatus = 'loaded';
            this.preloadVideoElement = videoElement;
            this.currentPreloadVideoSrc = targetSrc;
            return;
          }
        }
        
        // 检查请求频率，防止无限循环
        const now = Date.now();
        if (this.lastVideoRequestTime > 0) {
          const timeSinceLastRequest = now - this.lastVideoRequestTime;
          if (timeSinceLastRequest < 1000) {
            // 如果距离上次请求不到1秒，增加计数器
            this.videoRequestCount++;
            if (this.videoRequestCount > this.maxVideoRequestsPerSecond) {
              // 请求过于频繁，停止请求
              this.addSystemLog('视频请求过于频繁，已停止请求');
              this.videoPreloadStatus = 'error';
              this.isPreloadVideoRequesting = false;
              return;
            }
          } else {
            // 超过1秒，重置计数器
            this.videoRequestCount = 0;
          }
        }
        this.lastVideoRequestTime = now;
        
        this.videoPreloadStatus = 'loading';
        this.isPreloadVideoRequesting = true; // 设置请求锁
        
        // 开始预加载，使用 $nextTick 确保DOM已更新
        this.$nextTick(() => {
          // 再次检查URL和DOM元素
          if (!this.$refs.preloadVideo) {
            this.addSystemLog('视频预加载失败：DOM元素不存在');
            this.videoPreloadStatus = 'error';
            this.isPreloadVideoRequesting = false; // 重置请求锁
            return;
          }
          
          if (!this.preloadVideoUrl) {
            this.addSystemLog('视频预加载失败：URL为空');
            this.videoPreloadStatus = 'error';
            this.isPreloadVideoRequesting = false; // 重置请求锁
            return;
          }
          
          const videoElement = this.$refs.preloadVideo;
          const currentSrc = videoElement.src || '';
          const targetSrc = this.preloadVideoUrl;
          const readyState = videoElement.readyState;
          
          // 如果src已经设置且相同，检查视频状态
          if (currentSrc === targetSrc && currentSrc) {
            // src相同，检查视频是否已经加载
            if (readyState >= 3) {
              // 视频已经有足够数据，不需要重新加载
              this.videoPreloadStatus = 'loaded';
              this.preloadVideoElement = videoElement;
              this.currentPreloadVideoSrc = targetSrc;
              this.isPreloadVideoRequesting = false; // 重置请求锁
              return;
            }
            // src相同但未加载，需要调用load()
          } else {
            // src不同或为空，需要更新（只在真正变化时才设置）
            if (currentSrc !== targetSrc) {
              // 设置src，但不会自动加载（因为preload="none"）
              videoElement.src = targetSrc;
              this.currentPreloadVideoSrc = targetSrc;
            }
          }
          
          // 只在视频未加载时才调用load()
          // 由于preload="none"，必须手动调用load()才会开始加载
          if (readyState < 3) {
            this.videoLoadCallCount++;
            videoElement.load();
          } else {
            // 视频已加载，直接标记为已加载
            this.videoPreloadStatus = 'loaded';
            this.preloadVideoElement = videoElement;
            this.currentPreloadVideoSrc = targetSrc;
            this.isPreloadVideoRequesting = false; // 重置请求锁
          }
        });
      }, 500); // 延迟500ms
    },
    
    // 清理预加载视频（只在组件销毁时调用，其他时候只暂停不清空）
    cleanupPreloadVideo(forceCleanup = false) {
      // forceCleanup为true时才真正清理（用于组件销毁）
      // 平时只暂停视频，保持元素在内存中以便复用
      if (this.videoPreloadTimer) {
        clearTimeout(this.videoPreloadTimer);
        this.videoPreloadTimer = null;
      }
      
      if (this.$refs.preloadVideo) {
        const video = this.$refs.preloadVideo;
        // 先暂停视频
        video.pause();
        
        // 只有在强制清理时才清空src（组件销毁时）
        if (forceCleanup) {
          if (this.cleanupPreloadVideoTimer) {
            clearTimeout(this.cleanupPreloadVideoTimer);
            this.cleanupPreloadVideoTimer = null;
          }
          this.cleanupPreloadVideoTimer = setTimeout(() => {
            this.cleanupPreloadVideoTimer = null;
            if (video && !this.isVideoPlaying) {
              video.src = '';
              video.load();
            }
          }, 300);
          this.preloadVideoElement = null;
          this.videoPreloadStatus = 'idle';
        }
        // 否则只暂停，保持src不变，以便下次复用
      } else if (forceCleanup) {
        // 如果没有ref，但需要强制清理，清空状态
        this.preloadVideoElement = null;
        this.videoPreloadStatus = 'idle';
      }
    },
    
    // 处理视频播放状态变化
    handleVideoPlayingChanged(isPlaying) {
      this.isVideoPlaying = isPlaying;
    },
    
    // 处理视频播放结束（仅在 PLAY_TO_END 模式下由子组件触发）
    handleVideoEnded() {
      if (!this.isVideoPage) return;

      // 统一更新“当前视频是否在播放”的标记
      this.isVideoPlaying = false;

      const current = this.getCurrentVideoMeta();
      if (current && current.id) {
        // 自然播完时，将该视频的断点重置为 0，避免下次从结尾附近开始
        this.$set(this.videoResumeTimeById, current.id, 0);
      }

      const totalVideos = Array.isArray(this.videoList) ? this.videoList.length : 0;
      if (totalVideos <= 0) return;

      const mode = (this.rotationSeconds && this.rotationSeconds.videoSwitchMode) || 'FIXED_WINDOW';

      if (mode === 'PLAY_TO_END') {
        // 播完切下一个视频；最后一个视频播完后离开视频页。
        // 注意：只有在 PLAY_TO_END 模式下，子组件才会发出 video-ended 事件。
        const isLast = this.currentVideoIndex >= totalVideos - 1;
        if (!isLast) {
          this.currentVideoIndex += 1;
          return;
        }
        // 最后一个视频播完：重置索引，继续轮播到下一页
        this.currentVideoIndex = 0;
        this.rotateModules('next', { force: true, reason: 'video-ended' });
        return;
      }

      // FIXED_WINDOW 模式下：
      // - SleepViewVideo 内部使用 loop 播放，在当前页面停留窗口内自动循环；
      // - 页面切换仍然由轮播定时器控制（getRotationDurationMs / onBeforeRotate），
      //   这里不需要再根据 ended 事件去强制切页。
      // 如果有多个视频，也可以在这里选择“播完一个换下一个”；当前保持原有逻辑，
      // 形成列表内的轮播（只有少数浏览器/异常情况下才会触发到这里）。
      this.currentVideoIndex = (this.currentVideoIndex + 1) % totalVideos;
    },

    handleVideoTimeUpdate(payload) {
      const current = this.getCurrentVideoMeta();
      if (!current || !current.id) return;
      const t = payload && typeof payload.currentTime === 'number' ? payload.currentTime : 0;
      if (t >= 0) {
        this.$set(this.videoResumeTimeById, current.id, t);
      }
    },

    getCurrentVideoMeta() {
      if (!Array.isArray(this.videoList) || this.videoList.length === 0) return null;
      const idx = this.currentVideoIndex % this.videoList.length;
      return this.videoList[idx] || null;
    },

    // 轮播调度使用：返回当前页的展示时长（毫秒）
    getRotationDurationMs() {
      const s = this.rotationSeconds || {};
      if (this.isVideoPage) {
        const mode = s.videoSwitchMode || 'FIXED_WINDOW';
        // 视频播完再切：禁用定时轮播（由 video ended 驱动切页）
        if (mode === 'PLAY_TO_END') return 0;
        return (parseInt(s.videoPage) || 30) * 1000;
      }
      if (this.isFullscreenWelcomePage) return (parseInt(s.welcomeFullscreen) || 15) * 1000;
      return (parseInt(s.patientPage) || 15) * 1000;
    },

    // 轮播调度使用：到点切页前的钩子（用于视频断点）
    onBeforeRotate() {
      if (!this.isVideoPage) return;
      const current = this.getCurrentVideoMeta();
      const view = this.$refs.videoView;
      const player = view && view.$refs ? view.$refs.videoPlayer : null;
      if (current && current.id && player) {
        try {
          this.$set(this.videoResumeTimeById, current.id, player.currentTime || 0);
          player.pause();
        } catch (e) {
          // ignore
        }
      }
    },

    async fetchRotationSeconds() {
      const storeId = this.effectiveStoreId;
      if (!storeId) return;
      try {
        // 约定新接口：GET /api/screen-rotation-config/effective/{storeId}
        const apiUrl = config.getApiUrl(
          config.apiEndpoints.screenRotationConfig.effective,
          storeId
        );
        const response = await axios.get(apiUrl);
        const parsed = parseApiResponse(response);
        if (parsed.success && parsed.data) {
          const d = snakeToCamel(parsed.data);
          this.rotationSeconds = {
            welcomeFullscreen: parseInt(d.welcomeFullscreenSeconds || d.welcomeFullscreen || d.welcome_fullscreen_seconds) || this.rotationSeconds.welcomeFullscreen,
            patientPage: parseInt(d.patientPageSeconds || d.patientPage || d.patient_page_seconds) || this.rotationSeconds.patientPage,
            videoPage: parseInt(d.videoPageSeconds || d.videoPage || d.video_page_seconds) || this.rotationSeconds.videoPage,
            videoSwitchMode: d.videoSwitchMode || d.video_switch_mode || this.rotationSeconds.videoSwitchMode || 'FIXED_WINDOW'
          };
          // 配置变化后重启轮播，让新秒数立即生效
          this.updateRotation();
        }
      } catch (e) {
        // 拉取失败时使用默认值即可（不影响大屏运行）
      }
    },
    
    // 检查气泡是否过期（气泡生命周期为5秒）
    // 气泡过滤方法已提取到 useBubbles composable
    
    // 合并现有气泡数据到新数据中
    sortAppointmentsByRemainingTime() {
      this.checkUpcomingAppointments();

      this.addSystemLog('预约已按剩余治疗时间排序');
    },
    getMockData() {
      // 不再生成模拟预约记录，只保留脑波数据的虚拟生成功能
      // 预约记录应该完全来自真实的API数据
      return [];
    },
    
    parseTimeString(timeStr) {
      return sleepDataUtils.parseTimeString(timeStr);
    },
    
    // 基于指定日期解析时间字符串
    parseTimeStringWithDate(timeStr, appointmentDate) {
      return sleepDataUtils.parseTimeStringWithDate(timeStr, appointmentDate);
    },
    cleanCompletedAppointments() {
      // 这个方法现在只用于其他清理逻辑，不移除已完成的预约
      const now = new Date();
      let hasRemoved = false;
      let removedCount = 0;

      if (hasRemoved) {
        if (this.appointmentData.length > 0) {
          const displayCount = Math.min(this.maxDisplayCount, this.appointmentData.length);
          for (let i = 0; i < displayCount; i++) {
            if (!this.appointmentData[i].isNew) {
              this.appointmentData[i].isNew = true;

              this.addSystemLog(
                `显示预约：${this.appointmentData[i].roomName} - ${this.appointmentData[i].patientName}`
              );

              const timerId = setTimeout(() => {
                if (this.appointmentData[i]) {
                  this.appointmentData[i].isNew = false;
                }
              }, 3000);
              this.newAppointmentResetTimers.push(timerId);
            }
          }
        }

        const currentMockCount = this.appointmentData.filter((item) => item.isMock).length;

        // 如果没有数据，保持空状态
        if (this.appointmentData.length === 0) {
          this.addSystemLog('当前无预约数据');
        }
      } else {
        if (this.appointmentData.length === 0) {
          this.addSystemLog('数据为空，等待API数据');
        }
      }
    },

    // 轮播相关方法已提取到 usePageRotation composable（含 startSlideAnimation, cancelSlideAnimation, rotateModules）

    preloadNextPage(nextPage) {
      // 确定每页需要的数量
      const isHalfScreenWelcome = this.welcomeMessage && 
        !(this.welcomeConfig && (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1'));
      const pageSize = isHalfScreenWelcome ? 2 : 4;
      
      // 计算治疗中患者数量
      const ongoingPatients = this.activeAppointments.filter(item => 
        this.getTreatmentStatus(item) === '治疗中'
      );
      
      // 如果下一页是全屏欢迎语页，不需要预加载
      const patientPages = Math.ceil(ongoingPatients.length / pageSize);
      if (nextPage === patientPages) {
        return; // 全屏欢迎语页不需要预加载
      }
      
      // 获取下一页的治疗中患者
      const startIndex = nextPage * pageSize;
      const endIndex = startIndex + pageSize;
      const nextPageOngoing = ongoingPatients.slice(startIndex, endIndex);
      
      // 计算需要补位的数量
      const needToFill = pageSize - nextPageOngoing.length;
      
      // 获取已结束患者
      const completedPatients = this.activeAppointments.filter(item => 
        this.getTreatmentStatus(item) === '已结束'
      );
      
      // 计算前面页已经使用的已结束患者数量
      let usedCompletedCount = 0;
      for (let page = 0; page < nextPage; page++) {
        const pageStart = page * pageSize;
        const pageOngoing = ongoingPatients.slice(pageStart, pageStart + pageSize);
        const pageOngoingCount = pageOngoing.length;
        const pageNeedFill = Math.max(0, pageSize - pageOngoingCount);
        usedCompletedCount += pageNeedFill;
      }
      
      // 获取下一页可用的已结束患者
      const availableCompleted = completedPatients.slice(usedCompletedCount);
      const fillPatients = availableCompleted.slice(0, needToFill);
      
      const nextItems = [...nextPageOngoing, ...fillPatients];
      while (nextItems.length < pageSize) {
        nextItems.push(null);
      }
      nextItems.forEach((item, idx) => {
        if (!item) return;
        const charImg = this.getCharacterImage(item);
        this.preloadImage(charImg);
        const statusIcon = this.getTreatmentStatusIcon(item);
        this.preloadImage(statusIcon);
      });
      const view = this.$refs.sleepView;
      if (view && typeof view.preloadChartOptions === 'function') {
        view.preloadChartOptions(nextItems);
      }
    },
            

    getProgressPercentage(appointment) {
      return sleepViewUtils.getProgressPercentage(appointment);
    },

    markNewAppointments(newData) {
      if (this.appointmentData.length === 0) {
        return newData;
      }

      let hasNewAppointment = false;
      newData.forEach((appointment, index) => {
        const existingIndex = this.appointmentData.findIndex(
          (item) =>
            item.roomName === appointment.roomName &&
            item.patientName === appointment.patientName &&
            item.startTime === appointment.startTime &&
            item.endTime === appointment.endTime
        );

        if (existingIndex === -1) {
          appointment.isNew = true;
          hasNewAppointment = true;

          this.addSystemLog(
            `发现新预约：${appointment.roomName} - ${appointment.patientName}`
          );

          const timerId = setTimeout(() => {
            const index = this.appointmentData.findIndex(
              (item) =>
                item.roomName === appointment.roomName &&
                item.patientName === appointment.patientName &&
                item.startTime === appointment.startTime &&
                item.endTime === appointment.endTime
            );

            if (index !== -1) {
              this.appointmentData[index].isNew = false;
            }
          }, 3000);
          this.newAppointmentResetTimers.push(timerId);
        } else {
          appointment.hasShownStartNotification =
            this.appointmentData[existingIndex].hasShownStartNotification;
        }
      });

      if (hasNewAppointment) {
        this.updateRotation();
      }

      return newData;
    },
    getRemainingTime(appointment) {
      if (!appointment || !appointment.startTime || !appointment.endTime) {
        return '无数据';
      }

      const now = new Date();
      const startTime = this.parseTimeString(appointment.startTime);
      const endTime = this.parseTimeString(appointment.endTime);

      if (!startTime || !endTime) {
        return '时间格式错误';
      }

      if (now >= endTime) {
        return '已完成';
      }

      if (now < startTime) {
        const totalMinutes = Math.floor(
          (endTime.getTime() - startTime.getTime()) / (1000 * 60)
        );
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return hours > 0
          ? `${hours}小时${minutes}分钟`
          : `${minutes}分钟`;
      }

      // 移除治疗即将结束的逻辑，直接返回正在治疗中
      return '治疗中';
    },

    getActualRemainingTime(appointment) {
      return sleepDataUtils.getActualRemainingTime(appointment);
    },
    
    // 获取睡眠气泡值（随机值）
    getSleepBubbleValue(sleepStatus) {
      return sleepDataUtils.getSleepBubbleValue(sleepStatus);
    },
    
    // 生成基于患者信息的随机种子
    generatePatientSeed(item) {
      return sleepDataUtils.generatePatientSeed(item);
    },

    // 基于种子的伪随机数生成器
    seededRandom(seed) {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    },

    // 计算元气恢复分数
    calculateEnergyScore(item) {
      return sleepViewUtils.calculateEnergyScore(item);
    },

    updateSinglePatientBubble(patient) {
      const view = this.$refs.sleepView;
      if (view && view.updateSinglePatientBubble) {
        view.updateSinglePatientBubble(patient);
      }
    },
    
    // 气泡更新方法已提取到 useBubbles composable

    getRemainingTimeInMinutes(appointment) {
      return sleepDataUtils.getRemainingTimeInMinutes(appointment);
    },

    getTreatmentStatus(appointment) {
      return sleepDataUtils.getTreatmentStatus(appointment);
    },

    checkUpcomingAppointments() {
      const now = new Date();
      let hasNewAppointment = false;

      this.appointmentData.forEach((appointment) => {
        if (!appointment || !appointment.startTime || !appointment.endTime) {
          return;
        }

        const startTime = this.parseTimeString(appointment.startTime);
        const endTime = this.parseTimeString(appointment.endTime);

        if (!startTime || !endTime) {
          return;
        }

        if (now >= startTime) {
          const appointmentId = `${appointment.roomName}-${appointment.patientName}-${appointment.startTime}`;

          if (!appointment.hasShownStartNotification) {
            this.addSystemLog(
              `预约到点显示：${appointment.roomName} - ${appointment.patientName}`
            );

            appointment.hasShownStartNotification = true;

            appointment.isNew = true;

            hasNewAppointment = true;
          }
        }
      });

      if (hasNewAppointment) {
        const beforeSnapshot = this.appointmentData.map(app => ({
          roomId: app.roomId,
          delta: app.delta,
          theta: app.theta,
          alpha: app.alpha,
          beta: app.beta,
          gamma: app.gamma
        }));
        const tempData = [...this.appointmentData];
        console.log(`[数据赋值] 重新赋值appointmentData (触发新预约显示)`, {
          beforeSnapshot,
          afterCount: tempData.length
        });
        this.appointmentData = tempData;
      }
    },

    formatPatientName(patientName) {
      return sleepDataUtils.formatPatientName(patientName);
    },

    // 清理治疗师名称中的“（满级）”后缀，仅保留前缀名称
    formatTherapistName(name) {
      return sleepViewUtils.formatTherapistName(name);
    },

    getTreatmentStatusClass(item) {
      return sleepViewUtils.getTreatmentStatusClass(item);
    },

    getTreatmentStatusIcon(item) {
      return sleepViewUtils.getTreatmentStatusIcon(item);
    },

    formatTimeDisplay(timeStr) {
      return sleepViewUtils.formatTimeDisplay(timeStr);
    },
    
    getSleepStatus(item) {
      return sleepViewUtils.getSleepStatus(item);
    },
    
    // 根据性别和睡眠状态获取对应的人物图
    getCharacterImage(item) {
      return sleepViewUtils.getCharacterImage(item);
    },
    getBrainwaveDisplay(item, type) {
      return sleepViewUtils.getBrainwaveDisplay(item, type);
    },
    
    // 启动时间更新定时器
    startTimeUpdateTimer() {
      if (this.timeUpdateTimer) {
        clearInterval(this.timeUpdateTimer);
      }
      this.timeUpdateTimer = setInterval(() => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        this.currentTimeValue = `${hours}:${minutes}:${seconds}`;
      }, 1000);
    }
  },

  computed: {
    // 从 composables 合并的计算属性（displayedAppointments 先于 pageRotation，供 totalPages/isVideoPage 等依赖 activeAppointments）
    ...welcomeMessageComposable.computed,
    ...displayedAppointmentsComposable.computed,
    ...pageRotationComposable.computed,
    
    // 获取有效的门店ID：优先使用prop，如果没有则从登录信息中获取第一个门店
    effectiveStoreId() {
      if (this.storeId) {
        return this.storeId;
      }
      // 从登录信息中获取第一个门店ID
      const storeIds = getUserStoreIds();
      return storeIds.length > 0 ? storeIds[0] : null;
    },

    currentVideoResumeTime() {
      const current = this.getCurrentVideoMeta();
      if (!current || !current.id) return 0;
      const t = this.videoResumeTimeById && this.videoResumeTimeById[current.id];
      return typeof t === 'number' && isFinite(t) && t >= 0 ? t : 0;
    },
    helpers() {
      return {
        getTreatmentStatusClass: sleepViewUtils.getTreatmentStatusClass,
        getTreatmentStatus: sleepDataUtils.getTreatmentStatus,
        formatTimeDisplay: sleepViewUtils.formatTimeDisplay,
        formatTherapistName: sleepViewUtils.formatTherapistName,
        getCharacterImage: sleepViewUtils.getCharacterImage,
        calculateEnergyScore: sleepViewUtils.calculateEnergyScore,
        getBrainwaveDisplay: (item, type) => sleepViewUtils.getBrainwaveDisplay(item, type),
        getTreatmentStatusIcon: sleepViewUtils.getTreatmentStatusIcon,
        getSleepStatus: sleepViewUtils.getSleepStatus,
        getProgressPercentage: sleepViewUtils.getProgressPercentage,
        formatPatientName: sleepDataUtils.formatPatientName,
        addSystemLog: this.addSystemLog,
        getSleepBubbleValue: sleepDataUtils.getSleepBubbleValue,
        generateVirtualBrainwaveData: this.generateVirtualBrainwaveData
      };
    },

    currentDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}年${month}月${day}日`;
    },
    
    currentWeekday() {
      const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
      return weekdays[new Date().getDay()];
    },
    
    currentTime() {
      // 如果使用定时器更新的时间值存在，则使用它，否则生成新的时间
      if (this.currentTimeValue) {
        return this.currentTimeValue;
      }
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    },
    
    ongoingTreatmentCount() {
      try {
        const result = this.calculateTodayRecoveryCount();
         console.log('🧮 ongoingTreatmentCount 计算结果:', result);
        return result;
      } catch (error) {
         console.error('❌ ongoingTreatmentCount 计算异常:', error);
        return 0;
      }
    }
  },

  watch: {
    appointmentData: {
      handler(newData, oldData) {
        // 监控appointmentData变化，更新患者治疗状态
        if (newData && Array.isArray(newData)) {
          newData.forEach(appointment => {
            if (appointment.roomId) {
              const treatmentStatus = this.getTreatmentStatus(appointment);
              // 仅用于前端展示，无需更新模拟器状态；数据来源统一为后端
            }
          });
        }
      },
      deep: true,
      immediate: false
    },
    activeAppointments(newList, oldList) {
      // 使用 totalPages 计算属性，它会根据欢迎语状态动态计算
      // 但需要排除全屏欢迎语页，只计算患者模块页数
      const isHalfScreenWelcome = this.welcomeMessage && 
        !(this.welcomeConfig && (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1'));
      const pageSize = isHalfScreenWelcome ? 2 : 4;
      
      const ongoingPatients = (Array.isArray(newList) ? newList : []).filter(item => 
        this.getTreatmentStatus(item) === '治疗中'
      );
      const patientPages = Math.ceil(ongoingPatients.length / pageSize);
      
      const effectivePage = Math.max(0, Math.min(this.currentPage, Math.max(0, patientPages - 1)));
      if (this.isSliding) return;
      if (effectivePage !== this.currentPage) {
        this.startSlideAnimation('next', effectivePage, () => {
          this.initCharts();
        });
      }
    },
    currentPage(newPage, oldPage) {
      // 监听页面变化，在患者页面时开始预加载视频
      // 注意：避免频繁调用preloadVideo()，只在必要时调用
      if (newPage !== oldPage && !this.isVideoPage && !this.isFullscreenWelcomePage) {
        // 当前在患者页面，检查是否需要预加载
        // 只在视频URL已设置且未加载时才预加载
        if (this.preloadVideoUrl && this.videoPreloadStatus !== 'loading' && this.videoPreloadStatus !== 'loaded') {
          console.log('[currentPage Watcher] 页面变化，开始预加载视频', {
            page: newPage,
            url: this.preloadVideoUrl,
            status: this.videoPreloadStatus
          });
          if (this.videoPreloadTimer) {
            clearTimeout(this.videoPreloadTimer);
            this.videoPreloadTimer = null;
          }
          // 延迟预加载，避免与其他操作冲突
          this.videoPreloadTimer = setTimeout(() => {
            this.videoPreloadTimer = null;
            if (this.preloadVideoUrl && !this.isVideoPage) {
              this.preloadVideo();
            }
          }, 500);
        } else {
          console.log('[currentPage Watcher] 跳过预加载', {
            page: newPage,
            hasUrl: !!this.preloadVideoUrl,
            status: this.videoPreloadStatus
          });
        }
      } else if (this.isVideoPage) {
        // 切换到视频页，清理预加载定时器
        if (this.videoPreloadTimer) {
          clearTimeout(this.videoPreloadTimer);
          this.videoPreloadTimer = null;
        }
      }
    },
    videoList(newList, oldList) {
      // 视频列表变化时，更新预加载视频URL（只在URL真正变化时更新）
      // 注意：由于fetchVideoList已经做了数据比较，这里通常只在真正变化时才会触发
      
      console.log('[视频列表Watcher] 触发', {
        newListLength: newList ? newList.length : 0,
        oldListLength: oldList ? oldList.length : 0,
        currentUrl: this.preloadVideoUrl || '(空)',
        timestamp: new Date().toISOString()
      });
      
      if (newList && Array.isArray(newList) && newList.length > 0) {
        // 验证视频列表中的视频是否有有效的ID
        const validVideos = newList.filter(video => video && video.id);
        
        if (validVideos.length === 0) {
          // 视频列表中没有有效的视频，清空URL
          if (this.preloadVideoUrl) {
            console.log('[视频列表Watcher] 清空URL：没有有效视频');
            this.preloadVideoUrl = '';
            this.lastPreloadVideoUrl = '';
            this.videoPreloadStatus = 'idle';
            this.videoPreloadRetryCount = 0;
            this.addSystemLog('视频列表中没有有效的视频ID');
          }
          return;
        }
        
        // 计算新的URL
        const newUrl = this.getPreloadVideoUrl();
        
        // 只在URL真正变化时才更新，避免重复设置相同的src
        // 同时检查lastPreloadVideoUrl，避免重复调用
        if (newUrl !== this.preloadVideoUrl && newUrl !== this.lastPreloadVideoUrl) {
          console.log('[视频列表Watcher] URL变化，更新', {
            oldUrl: this.preloadVideoUrl || '(空)',
            newUrl: newUrl || '(空)',
            lastUrl: this.lastPreloadVideoUrl || '(空)'
          });
          // 更新URL（内部会检查URL是否变化，只在变化时才更新，并延迟调用preloadVideo）
          this.updatePreloadVideoUrl();
        } else {
          console.log('[视频列表Watcher] URL未变化，跳过', {
            currentUrl: this.preloadVideoUrl || '(空)',
            newUrl: newUrl || '(空)',
            lastUrl: this.lastPreloadVideoUrl || '(空)'
          });
        }
        // 如果URL相同，不进行任何操作，避免重复预加载
      } else {
        // 没有视频时，只在URL不为空时才清空，避免重复操作
        if (this.preloadVideoUrl) {
          console.log('[视频列表Watcher] 清空URL：列表为空');
          this.preloadVideoUrl = '';
          this.lastPreloadVideoUrl = '';
          this.videoPreloadStatus = 'idle';
          this.videoPreloadRetryCount = 0;
        }
        // 不清理预加载视频元素（保持复用）
        // 因为视频列表可能只是暂时为空，很快又会恢复
        // 只在组件销毁时才清理
      }
    }
  }
};
</script>

<style src="@/components/sleep-styles.css"></style>

<style>
.main-content-wrapper .digit { font-size: 19px !important; }
.main-content-wrapper .current-count { font-size: 16px !important; }
.main-content-wrapper .patient-name-container span { font-size: 16px !important; }
.main-content-wrapper .therapist-info { font-size: 14px !important; }
.main-content-wrapper .score-numerator { font-size: 10px !important; }
.main-content-wrapper .score-separator { font-size: 10px !important; }
.main-content-wrapper .score-denominator { font-size: 10px !important; }
.main-content-wrapper .bubble-text { font-size: 16px !important; line-height: 30px !important; }
</style>
<style>
/* 欢迎语样式 */
.welcome-message-container {
  height: 50%;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  background-image: url('@/assets/bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  overflow: hidden;
  transition: all 0.5s ease-in-out; /* 添加平滑过渡动画 */
  transform-origin: top; /* 设置变换原点为顶部 */
}

/* 欢迎语进入动画 */
.welcome-message-container.welcome-enter {
  animation: welcomeSlideDown 0.5s ease-out forwards;
}

/* 欢迎语退出动画 */
.welcome-message-container.welcome-leave {
  animation: welcomeSlideUp 0.5s ease-in forwards;
}

.welcome-message {
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  padding: 20px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transform: translateY(0);
  transition: all 0.3s ease-in-out; /* 添加文字动画 */
  font-size: var(--welcome-font-size, 22px); /* 基准字号，随整页缩放；允许外部覆盖 */
  line-height: 1.4;
  
  /* 容器设置 */
  overflow: hidden;
  max-width: 90%;
  position: relative;
}

.scrolling-container {
  display: flex;
  width: max-content;
  animation: seamlessScroll 20s linear infinite;
}

.scrolling-text {
  white-space: nowrap;
  display: inline-block;
  flex-shrink: 0;
  padding-right: 4em; /* 增加间距确保无缝衔接 */
  font-size: inherit; /* 继承欢迎语容器的字号，支持外部覆盖 */
}

/* 无缝循环滚动动画 - 真正的无缝循环 */
@keyframes seamlessScroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* 欢迎语文字进入动画 */
.welcome-message.message-enter {
  animation: messageSlideIn 0.6s ease-out 0.2s both;
}

/* 欢迎语文字退出动画 */
.welcome-message.message-leave {
  animation: messageSlideOut 0.3s ease-in both;
}

/* 主要内容区域样式调整 */
.main-content-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: height 0.5s ease-in-out; /* 添加平滑过渡 */
}

.main-content-wrapper.with-welcome {
  height: 100%;
}

/* 顶部信息栏样式调整 */
.top-info {
  height: 8%;
  transition: all 0.3s ease-in-out;
}

/* 当有欢迎语时，调整顶部信息栏 */
.main-content-wrapper.with-welcome .top-info {
  height: 8%;
  top: 50%;
}

/* 当有欢迎语时，调整顶部信息栏内的字体 */
.main-content-wrapper.with-welcome .top-info * {
  font-size: 0.9em !important; /* 使用固定比例而非变量 */
}

/* 全局根元素设置 */
html {
  font-size: 16px; /* 基础字体大小，用于rem计算 */
  margin: 0;
  padding: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

/* CSS变量定义 - 移除缩放变量，不再使用 */
:root {
  --scale: 1;
}

body {
  margin: 0;
  padding: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.big-screen-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/bg.png');
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  font-family: 'Microsoft YaHei', Arial, sans-serif;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

/* 红色顶部模块 - 根据是否有欢迎语调整位置 */
.top-info{
  width: 100%;
  height: 8%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  box-sizing: border-box;
  overflow: hidden;
  padding: 0 1rem;
  transition: top 0.3s ease;
}

/* 当有欢迎语时，顶部信息栏下移 */
.main-content-wrapper.with-welcome .top-info {
  top: 50%;
}

.date-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 10rem;
  margin-left: 20px;
  margin-top: 0px; /* 增加距离顶部的距离 */
}

.date {
  font-size: 16px;
  margin-bottom: 0.25rem;
  color: #794112;
}

.top-wd{
  display: flex;
  gap: 1rem;
  align-items: center;
}

.weekday {
  font-size: 16px;
  margin-bottom: 0.25rem;
  color: #794112;
}

.time {
  font-size: 32px;
  font-weight: bold;
  font-family: 'Digital', monospace;
  color: #794112;
  line-height: 1;
}

.top-bar {
  width: 50%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start; /* 图片贴顶显示 */
}

.top-bar img {
  width: 70%;
  height: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: top;
  margin: 0 0 0 80px;
  display: block;
}

.title {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin: 0 auto;
}

.current-count {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.125rem;
  color: #794112;
  font-weight: bold;
  min-width: 10rem;
  justify-content: flex-end;
}

.digit-container {
  display: flex;
  gap: 0.3rem; /* 进一步收紧数字间距以压缩宽度 */
  padding: 0.15rem; /* 进一步减少容器内边距 */
  min-width: auto; /* 自动宽度 */
  min-height: 1.8rem; /* 进一步调整最小高度 */
  justify-content: center;
  align-items: center; /* 添加垂直居中 */
  border-radius: 0.25rem; /* 添加圆角 */
}

.digit {
  font-size: 19px;
  color: #ffffff;
  text-shadow: 0.125rem 0.125rem 0.25rem rgba(0, 0, 0, 0.8);
  margin: 0;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.4rem;
  min-height: 1.4rem;
  background-image: url('@/assets/计数器背景.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  padding: 0.4rem;
  border-radius: 0.25rem;
}

.system-logs-container {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: min(26%, 300px);
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 0.5rem;
  color: white;
  z-index: 1000;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.log-header {
  background-color: #333;
  padding: 0.5rem 0.75rem;
  font-weight: bold;
  border-bottom: 0.0625rem solid #555;
  font-size: 1rem;
}

.log-list {
  max-height: 12%;
  overflow-y: auto;
}

.log-item {
  padding: 0.375rem 0.75rem;
  border-bottom: 0.0625rem solid #444;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
}

.log-time {
  color: #aaa;
  margin-right: 0.625rem;
  font-family: monospace;
}

.log-message {
  flex: 1;
}

.no-logs {
  padding: 0.625rem;
  text-align: center;
  color: #888;
  font-style: italic;
}

/* 模块容器 - 已修改为与顶部无缝衔接 */
.modules-wrapper {
  width: 100%;
  height: 81%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 0.8rem;
  padding: 0.8rem;
  box-sizing: border-box;
  overflow: hidden !important; /* 确保内容不会溢出 */
  position: absolute;
  will-change: transform, opacity; /* 优化动画性能 */
  backface-visibility: hidden; /* 防止动画闪烁 */
  top: 8%;
  left: 0;
  right: 0; /* 添加right定位 */
  width: 100%;
  transform: translateZ(0); /* 触发GPU加速 */
  transition: top 0.3s ease, height 0.3s ease; /* 添加过渡动画 */
  text-align: center;
  isolation: isolate !important; /* 创建新的层叠上下文 */
  contain: layout style paint !important; /* CSS containment */
  z-index: 0 !important; /* 确保层级正确 */
}

/* 当有欢迎语时，调整模块容器的位置和高度 */
.main-content-wrapper.with-welcome .modules-wrapper {
  top: calc(50% + 10%);
  height: calc(50% - 10%);
  grid-template-rows: repeat(2, auto);
  gap: 0.8rem;
  padding: 0.48rem;
  grid-auto-rows: auto;
  overflow: hidden; /* 修复：保持 hidden 防止内容穿透 */
}

/* 当有欢迎语时，调整模块内所有字体大小 */
.main-content-wrapper.with-welcome .modules-wrapper * {
  font-size: 0.9em !important; /* 使用固定比例 */
}

/* 当有欢迎语时，调整患者信息模块高度 */
.main-content-wrapper.with-welcome .character-section {
  width: 30% !important;
  height: clamp(210px, 26%, 260px) !important;
}

.main-content-wrapper.with-welcome .patient-huanzhe {
  height: clamp(90px, 11%, 120px);
  padding: 0.4rem;
}

/* 当有欢迎语时，调整脑波图表区域 */
.main-content-wrapper.with-welcome .middle-info {
  gap: 0.32rem;
  height: clamp(200px, 22%, 240px) !important;
  width: 14.4% !important;
}

.main-content-wrapper.with-welcome .wave-chart-wrapper {
  height: clamp(170px, 20%, 210px) !important;
}

/* 当有欢迎语时，调整模块内的间距和内边距 */
.main-content-wrapper.with-welcome .frame-item {
  padding: 0.8rem;
  margin: 0.4rem;
  width: 100%;
}

/* 当有欢迎语时，调整睡眠状态模块背景图 */
.main-content-wrapper.with-welcome .sleep-status {
  background-size: contain; /* 改为contain以保持背景图完整性 */
  background-position: center;
  background-repeat: no-repeat;
}

/* 当有欢迎语时，调整图表容器高度 */
.main-content-wrapper.with-welcome .chart-container {
  height: 100%; /* 保持100%高度 */
}

/* 当有欢迎语时，调整按钮和控件大小 */
.main-content-wrapper.with-welcome button,
.main-content-wrapper.with-welcome .control-button {
  font-size: 0.9em !important; /* 使用固定比例 */
  padding: 0.5rem 1rem !important; /* 固定内边距 */
}

.frame-item {
  border-radius: 1.25rem;
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  border: 0.0625rem solid #f5f5f5;
  background-size: cover;
  background-position: center;
  min-height: 300px;
  overflow: hidden !important; /* 修复：确保内容不会穿透 */
  position: relative !important; /* 修复：添加相对定位以支持isolation */
  isolation: isolate !important; /* 修复：创建新的层叠上下文，防止Canvas穿透 */
  contain: layout style paint !important; /* 严格的containment，确保完全隔离 */
  z-index: 0 !important; /* 确保层级正确 */
  clip-path: inset(0) !important; /* 强制裁剪，防止内容溢出 */
  box-sizing: border-box; /* 确保padding不会增加总尺寸 */
  position: relative;
  z-index: 1; /* 修复：添加 z-index 确保层级正确 */
  will-change: transform, opacity; /* 硬件加速提示 */
  transform: translateZ(0); /* 触发GPU加速 */
  backface-visibility: hidden;
  isolation: isolate; /* 修复：创建新的层叠上下文，防止子元素穿透 */
}

.frame-item:has(.top-content.treatment-ongoing) {
  background-image: url('~@/assets/card_bg_1.png');
}

.frame-item:has(.top-content.treatment-completed) {
  background-image: url('~@/assets/card_bg_2.png');
}

.fade-in {
  animation: fadeInSlideUp 1.2s ease forwards;
}

@keyframes fadeInSlideUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  50% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.main-content {
  display: flex;
  gap: 0.8rem;
  margin-bottom: 0.5rem;
  height: clamp(220px, 26%, 260px);
  box-sizing: border-box;
  margin-top: 10px;
}

/* 左侧人物图区域 */

.character-map {
  width: 100%;
  height: 85%; /* 增加高度从70%到85% */
  border-radius: 0.25rem;
  margin-top: 0.25rem;
  border: 1px solid #eeeeee00;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.character-map img {
  width: 90%; /* 增加宽度从auto到90% */
  height: 90%; /* 增加高度从100%到90%以保持比例 */
  max-width: 100%;
  border-radius: 0.25rem;
  object-fit: contain;
}

.patient-info {
  text-align: center;
  font-size: 12px;
  color: #666;
  line-height: 1.2;
}

.patient-info div {
  margin-bottom: 1px;
}

.character-section{
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 30% !important;
  height: clamp(210px, 26%, 260px) !important;
  box-sizing: border-box;
}

.patient-huanzhe{
  margin-bottom: 0;
  border-radius: 0.75rem;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: clamp(90px, 11%, 120px);
  background-color: #fff;
  z-index: 10;
  box-sizing: border-box;
  padding: 0.4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.patient-name-container {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 0;
  margin-left: 0;
  position: relative;
  border-radius: 0.25rem;
  box-sizing: border-box;
  flex: 1;
}

.sleep-status {
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  font-weight: bold;
  color: #ffffff;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  min-width: 2rem;
  text-align: center;
  box-sizing: border-box;
}

.ongoing-status {
  background-image: url('@/assets/tag_bg2.png');
  background-size: cover;
  background-position: center;
}

.completed-status {
  background-image: url('@/assets/tag_bg.png');
  background-size: cover;
  background-position: center;
}

.gender-icon {
  width: 16px;
  height: 16px;
  margin-right: 5px;
}

/* 中间信息区域 */
.middle-info {
  width: 18%;
  height: clamp(200px, 22%, 240px);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0;
  box-sizing: border-box;
}

.room-info {
  font-weight: bold;
  font-size: calc(14px * var(--scale));
  text-align: center;
  width: 70px;
  height: 26px;
  margin-top: 4px;
}
.top-content{
  width: 100%;
  height: 36px;
  display: flex;
  justify-content: space-between;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
}

.treatment-therapist-container {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 70%;
}

.top-content.treatment-ongoing {
  background-image: url('@/assets/card_title_bg_1.png');
  color: #794112;
}

.top-content.treatment-completed {
  background-image: url('@/assets/card_title_bg_2.png');
  color: #946BE7;
}

.room-info {
  display: flex;
  align-items: center;
  justify-content: center; /* 添加水平居中 */
  gap: 5px;
}

.status-icon {
  width: 16px;
  height: 16px;
}

.time-info {
  font-size: calc(13px * var(--scale));
  font-weight: bold;
  width: 170px;
  height: 22px;
  margin-top: 5px;
}

.therapist-info {
  font-size: calc(14px * var(--scale));
  font-weight: bold;
  width: 180px;
  height: 22px;
  margin-top: 5px;
}

.brain-waves {
  width: 100%;
  height: 60%;
  height: auto;
  box-sizing: border-box;
  
}

/* 当有欢迎语时，调整脑波值显示区域 */
.main-content-wrapper.with-welcome .brain-waves {
  margin-top: 0.4rem; /* 固定外边距 */
  padding: 0.6rem; /* 固定内边距 */
}

.wave-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto auto;
  gap: 0.6rem;
  box-sizing: border-box;
}

.wave-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.wave-label {
  font-size: 12px;
  color: #2F4059;
  margin-bottom: 0.2rem;
}

.wave-value {
  font-size: 16px;
  font-weight: bold;
  color: #000;
}

.wave-row span {
  color: #666;
}

.wave-value {
  color: #333 !important;
  font-weight: bold;
  min-width: 75px;
}

/* 脑波图表区域 */
.wave-chart-wrapper {
  width: 400px; /* 固定宽度，保持和第三张图的长度一样 */
  max-width: 90%; /* 最大宽度限制，避免在小屏幕上溢出 */
  height: clamp(210px, 24%, 240px);
  background-color: #FFFFFF;
  margin-left: 0;
  margin-right: 0;
  margin: 0 auto;
  margin-bottom: 0.4rem;
  border-radius: 1rem;
  box-sizing: border-box;
  overflow: hidden !important; /* 修复：确保脑波图不会穿透 */
  position: relative !important; /* 修复：添加相对定位 */
  z-index: 1 !important; /* 修复：确保层级正确 */
  isolation: isolate !important; /* 修复：创建新的层叠上下文 */
  contain: strict !important; /* 严格的containment，确保完全隔离 */
  clip-path: inset(0) !important; /* 强制裁剪，防止内容溢出 */
}

/* 当有欢迎语时，调整脑波图表区域的高度和缩放 */
.main-content-wrapper.with-welcome .wave-chart-wrapper {
  height: clamp(200px, 22%, 240px) !important;
  width: 400px !important; /* 固定宽度，保持和第三张图的长度一样 */
  max-width: 90% !important; /* 最大宽度限制 */
  margin: 0 auto 0.4rem !important;
  border-radius: 1rem !important;
}

.main-content-wrapper .time { font-size: 32px !important; }
.main-content-wrapper .weekday { font-size: 16px !important; }
.main-content-wrapper .date { font-size: 16px !important; }
.main-content-wrapper .wave-label { font-size: 13px !important; }
.main-content-wrapper .wave-value { font-size: 18px !important; }
.main-content-wrapper .progress-label { font-size: 9px !important; }
.main-content-wrapper .progress-score { font-size: calc(12px * var(--scale)) !important; }
.main-content-wrapper .progress-percentage { font-size: calc(14px * var(--scale)) !important; }
.main-content-wrapper .sleep-status { font-size: 12px !important; }

.wave-chart {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-size: contain; /* 改为contain，让背景图完整显示，不遮挡波线 */
  background-position: center;
  background-repeat: no-repeat;
  margin: 0;
  box-sizing: border-box;
  position: relative; /* 修复：添加相对定位以支持isolation */
  isolation: isolate; /* 修复：创建新的层叠上下文 */
  contain: layout style paint; /* 修复：CSS containment，进一步隔离渲染 */
}

/* 当有欢迎语时，调整脑波图背景的缩放 */
.main-content-wrapper.with-welcome .wave-chart {
  background-size: contain !important; /* 改为contain，让背景图完整显示 */
  background-position: center !important;
  background-repeat: no-repeat !important;
}

/* 确保背景图在所有情况下都能显示 */
.wave-chart.treatment-ongoing {
  background-image: url('@/assets/脑波图_方格背景.png') !important;
  background-color: transparent !important;
  background-size: contain !important; /* 改为contain，让背景图完整显示，不遮挡波线 */
  background-position: center !important;
  background-repeat: no-repeat !important;
}

.wave-chart.treatment-completed {
  background-image: url('@/assets/脑波图_方格背景_2.png') !important;
  background-color: transparent !important;
  background-size: contain !important; /* 改为contain，让背景图完整显示，不遮挡波线 */
  background-position: center !important;
  background-repeat: no-repeat !important;
}

/* 确保即使没有类名，也有默认背景（治疗中状态） */
.wave-chart:not(.treatment-completed):not(.treatment-ongoing) {
  background-image: url('@/assets/脑波图_方格背景.png') !important;
  background-color: transparent !important;
  background-size: contain !important; /* 改为contain，让背景图完整显示 */
  background-position: center !important;
  background-repeat: no-repeat !important;
}

/* 当有欢迎语时，调整脑波图背景图的缩放 */
.main-content-wrapper.with-welcome .wave-chart.treatment-ongoing,
.main-content-wrapper.with-welcome .wave-chart.treatment-completed {
  background-size: contain !important; /* 改为contain，让背景图完整显示 */
}

.echarts-container {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background: transparent !important;
  position: relative !important; /* 修复：添加相对定位 */
  overflow: hidden !important; /* 修复：防止Canvas视觉外溢 */
  z-index: 1 !important; /* 修复：确保层级正确 */
  will-change: transform, opacity; /* 硬件加速提示 */
  transform: translateZ(0); /* 触发GPU加速 */
  backface-visibility: hidden;
  overflow: hidden !important; /* 修复：确保内容不会溢出 */
  isolation: isolate !important; /* 修复：创建新的层叠上下文，防止Canvas穿透到其他模块 */
  contain: strict !important; /* 严格的containment，确保Canvas完全隔离 */
  clip-path: inset(0) !important; /* 强制裁剪，防止内容溢出 */
}

/* 确保ECharts生成的Canvas元素被正确限制在容器内 */
.echarts-container canvas {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 1 !important;
  pointer-events: none !important; /* 防止Canvas捕获事件导致穿透 */
  contain: strict !important; /* 严格的containment */
  clip-path: inset(0) !important; /* 强制裁剪 */
}

/* 当有欢迎语时，调整echarts容器的缩放 */
.main-content-wrapper.with-welcome .echarts-container {
  width: 100% !important;
  height: 100% !important;
  /* 移除transform缩放，保持原始大小 */
}

.bottom-content {
  margin-top: auto;
  padding-top: 0.25rem;
  width: 100%;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  min-height: 52px;
}

/* 当有欢迎语时，确保底部内容区域不会被隐藏 */
.main-content-wrapper.with-welcome .bottom-content {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
  height: auto !important;
  min-height: 52px !important; /* 确保数值有足够显示空间 */
  padding-top: 0.15rem !important; /* 固定上边距 */
}

.progress-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

/* 当有欢迎语时，确保进度容器不会被压缩 */
.main-content-wrapper.with-welcome .progress-container {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
  gap: 0.15rem !important; /* 减少间距，为数值显示腾出空间 */
  min-height: 38px !important; /* 调整最小高度 */
}

.progress-info {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
}

.progress-label {
  font-size: 9px;
  font-weight: bold;
  color: #333;
  flex: 0 1 auto;
}

.progress-score {
  font-size: 12px;
  font-weight: bold;
  color: #AA6F39;
  margin-right: 0.5rem;
  flex: 0 1 auto;
  white-space: nowrap;
}

/* 当有欢迎语时，确保进度条下面的数值不会被压缩消失 */
.main-content-wrapper.with-welcome .progress-info {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
  margin-top: 0.1rem !important; /* 增加上边距，确保与进度条有适当间距 */
  height: auto !important;
  min-height: 18px !important; /* 设置最小高度确保数值显示空间 */
}

.main-content-wrapper.with-welcome .progress-label {
  font-size: 9px !important;
  font-weight: bold !important;
  color: #333 !important;
}

.main-content-wrapper.with-welcome .progress-score {
  font-size: 10px !important;
  font-weight: bold !important;
  color: #AA6F39 !important;
  margin-right: 0.3rem !important;
}

.frame-item:has(.top-content.treatment-ongoing) .score-numerator {
  color: #AA6F39;
}

.frame-item:has(.top-content.treatment-completed) .score-numerator {
  color: #8354E3;
}

.score-separator,
.score-denominator {
  color: #00132F;
}

/* 当有欢迎语时，确保进度值的各个部分都能完整显示 */
.main-content-wrapper.with-welcome .score-numerator {
  font-size: 10px !important;
  font-weight: bold !important;
  display: inline !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.main-content-wrapper.with-welcome .score-separator {
  font-size: 10px !important;
  color: #00132F !important;
  display: inline !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.main-content-wrapper.with-welcome .score-denominator {
  font-size: 10px !important;
  color: #00132F !important;
  display: inline !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.progress-text {
  margin-left: 10px;
  text-align: left;
}

.progress-percentage {
  text-align: right;
  margin-right: 10px;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  font-size: 14px;
  font-weight: bold;
}

/* 当有欢迎语时，进度条百分比不要过度缩放 */
.main-content-wrapper.with-welcome .progress-percentage {
  font-size: 12px !important;
}

.custom-progress-bar {
  height: clamp(18px, 2.5%, 19px);
  width: 100%; /* 增加宽度从95%到100% */
  background-color: #e0e0e0;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #ddd;
  margin: 0 auto;
  box-sizing: border-box;
}

/* 当有欢迎语时，压缩进度条高度以节省空间 */
.main-content-wrapper.with-welcome .custom-progress-bar {
  height: 16px !important;
}

.progress-track {
  width: 100%;
  height: 100%;
  background-color: #e0e0e0;
  border-radius: 0.5rem;
  overflow: hidden;
  box-sizing: border-box;
}

.progress-fill {
  height: 100%;
  border-radius: 0.5rem;
  transition: width 0.3s ease;
  position: relative;
  box-sizing: border-box;
}

.frame-item:has(.top-content.treatment-ongoing) .progress-fill {
  background: linear-gradient(90deg, #AA6F39 0%, rgba(170, 111, 57, 0.3) 100%);
}

.frame-item:has(.top-content.treatment-completed) .progress-fill {
  background: linear-gradient(90deg, #956AE8 0%, rgba(149, 106, 232, 0.3) 100%);
}

.progress-glow {
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.6) 50%,
    transparent 100%
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* 气泡容器样式 */
.bubble-container {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden; /* 防止气泡超出容器 */
}

/* 睡眠气泡样式 */
.sleep-bubble {
  position: absolute;
  width: 30px; /* 固定气泡尺寸 */
  height: 30px; /* 固定气泡尺寸 */
  flex: 0 0 30px; /* 防止flex造成尺寸变化 */
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: bubbleFloat 4s linear forwards; /* 单向上浮动动画，不循环 */
  transition: opacity 1.5s ease-in-out; /* 平滑的透明度过渡效果 */
  z-index: 10; /* 提高z-index确保气泡可见 */
  /* 移除bottom，避免与模板中的top/right/left冲突导致动画异常 */
  will-change: transform, opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
  contain: layout paint size; /* 隔离布局与尺寸，避免父级影响 */
}

.sleep-bubble:nth-child(2) {
  animation-delay: 0.5s;
}

.bubble-bg {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: url('~@/assets/bubble_bg.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  z-index: 1;
}

.bubble-text {
  position: relative;
  z-index: 2;
  font-size: 16px !important;
  font-weight: bold;
  color: #743C0B;
  line-height: 30px;
}

@keyframes bubbleFloat {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-180px); /* 使用固定像素，避免rem引起的偶发计算问题 */
  }
}

/* 媒体查询 - 针对1152x768分辨率优化 */
@media screen and (width: 1152px) and (height: 768px) {
  .big-screen-container {
    padding: 0; /* 移除整体容器的内边距 */
  }
  
  .top-info {
    height: 10%;
    padding: 0 1.5rem;
  }
  
  .modules-wrapper {
    height: 90%;
    gap: 1.5rem;
    top: 10%;
  }
  
  .frame-item {
    padding: 1rem;
  }
  
  .main-content {
    gap: 1rem;
  }
  
  .character-section {
    width: 40%;
  }
  
  .middle-info {
    width: 60%;
  }
  
  .wave-grid {
    gap: 0.75rem;
  }
  
  .brain-waves {
    padding: 0.75rem;
  }
}

/* 媒体查询 - 小屏幕适配 */
@media screen and (max-width: 1200px) {
  .modules-wrapper {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    top: 8%;
  }
  
  .character-section {
    width: 30%;
  }
  
  .wave-grid {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: auto auto;
    gap: 0.5rem;
  }
}

/* 媒体查询 - 超小屏幕适配 */
@media screen and (max-width: 800px) {
  .top-info {
    flex-direction: column;
    height: auto;
    gap: 1rem;
    padding: 1rem;
  }
  
  .modules-wrapper {
    margin-top: auto; /* 适应顶部栏高度变化 */
    height: auto;
    min-height: calc(100% - 120px);
  }
  
  .main-content {
    flex-direction: column;
  }
  
  .character-section {
    width: 100%;
    height: 40%;
  }
  
  .middle-info {
    width: 100%;
    height: 60%;
  }
}
</style>
